// src/pages/WebhooksTab.js
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabaseClient'
import { CheckCircle } from 'lucide-react'
import { Spinner, TableWrap, Th, Td, formatTime, SectionTitle } from '../components/ui'

export function WebhooksTab() {
  const [rows,     setRows]     = useState([])
  const [loading,  setLoading]  = useState(true)
  const [resolving, setResolving] = useState(null)
  const [showAll,  setShowAll]  = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const q = supabase
      .from('failed_webhooks')
      .select('*')
      .order('created_at', { ascending: false })
    if (!showAll) q.eq('resolved', false)
    const { data } = await q
    if (data) setRows(data)
    setLoading(false)
  }, [showAll])

  useEffect(() => { load() }, [load])

  const resolve = async (id) => {
    setResolving(id)
    await supabase.rpc('mark_webhook_resolved', { p_webhook_id: id })
    await load()
    setResolving(null)
  }

  if (loading) return <Spinner />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <SectionTitle>Failed webhooks</SectionTitle>
        <label className="flex items-center gap-2 text-gray-500 text-xs cursor-pointer">
          <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)}
            className="accent-orange-500" />
          Show resolved
        </label>
      </div>

      <TableWrap>
        <thead>
          <tr>
            <Th>ID</Th>
            <Th>TXN ID</Th>
            <Th>Order</Th>
            <Th>Canteen</Th>
            <Th right>Amount</Th>
            <Th>Reason</Th>
            <Th>Created</Th>
            <Th>Resolved</Th>
            <Th></Th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="hover:bg-white/[0.02] transition-colors">
              <Td mono><span className="text-gray-500">#{r.id}</span></Td>
              <Td mono><span className="text-gray-300">{r.txn_id?.slice(0,16) || '—'}…</span></Td>
              <Td mono><span className="text-gray-400">{r.order_id?.slice(0,10) || '—'}…</span></Td>
              <Td><span className="text-gray-400">{r.canteen_id || '—'}</span></Td>
              <Td right><span className="text-orange-400">₹{r.amount || '—'}</span></Td>
              <Td>
                <span className={`text-[10px] font-mono ${r.reason?.includes('amount') ? 'text-red-400' : 'text-yellow-400'}`}>
                  {r.reason?.slice(0, 40) || '—'}
                </span>
              </Td>
              <Td><span className="text-gray-500">{formatTime(r.created_at)}</span></Td>
              <Td>
                {r.resolved
                  ? <CheckCircle className="w-4 h-4 text-green-400" />
                  : <span className="text-red-400 text-[10px] font-bold">OPEN</span>}
              </Td>
              <Td>
                {!r.resolved && (
                  <button onClick={() => resolve(r.id)} disabled={resolving === r.id}
                    className="text-[10px] font-bold uppercase tracking-widest text-green-400 hover:text-green-300 disabled:opacity-50">
                    {resolving === r.id ? '...' : 'Resolve'}
                  </button>
                )}
              </Td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={9} className="text-center text-gray-600 py-8 text-xs">
              {showAll ? 'No webhooks' : 'No unresolved webhooks'}
            </td></tr>
          )}
        </tbody>
      </TableWrap>
    </div>
  )
}


// src/pages/RefundsTab.js
export function RefundsTab() {
  const [rows,    setRows]    = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.rpc('get_refunds_overview').then(({ data }) => {
      if (data) setRows(data)
      setLoading(false)
    })
  }, [])

  if (loading) return <Spinner />

  const SLA_HOURS = 48

  return (
    <div className="space-y-4">
      <SectionTitle>Refund tracker</SectionTitle>
      <TableWrap>
        <thead>
          <tr>
            <Th>ID</Th>
            <Th>Order</Th>
            <Th>Canteen</Th>
            <Th>TXN ID</Th>
            <Th right>Amount</Th>
            <Th>Status</Th>
            <Th>Reason</Th>
            <Th>Created</Th>
            <Th right>Hours</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const overSla = (r.status === 'INITIATED' || r.status === 'PROCESSING') && r.hours_in_status > SLA_HOURS
            return (
              <tr key={r.refund_id} className={`hover:bg-white/[0.02] transition-colors ${overSla ? 'bg-amber-500/5' : ''}`}>
                <Td mono><span className="text-gray-500">#{r.refund_id}</span></Td>
                <Td mono><span className="text-gray-400">{r.order_id?.slice(0,10)}…</span></Td>
                <Td><span className="text-gray-300">{r.canteen_name}</span></Td>
                <Td mono><span className="text-gray-500">{r.txn_id?.slice(0,16) || '—'}…</span></Td>
                <Td right><span className="text-orange-400">₹{r.amount}</span></Td>
                <Td>
                  <span className={`text-[10px] font-bold uppercase tracking-widest ${
                    r.status === 'SUCCESS' ? 'text-green-400' :
                    r.status === 'FAILED' ? 'text-red-400' :
                    r.status === 'PROCESSING' ? 'text-blue-400' : 'text-yellow-400'
                  }`}>{r.status}</span>
                </Td>
                <Td><span className="text-gray-500">{r.reason}</span></Td>
                <Td><span className="text-gray-500">{formatTime(r.created_at)}</span></Td>
                <Td right>
                  <span className={overSla ? 'text-amber-400 font-bold' : 'text-gray-500'}>
                    {r.hours_in_status}h {overSla ? '⚠' : ''}
                  </span>
                </Td>
              </tr>
            )
          })}
          {rows.length === 0 && (
            <tr><td colSpan={9} className="text-center text-gray-600 py-8 text-xs">No refunds</td></tr>
          )}
        </tbody>
      </TableWrap>
      <p className="text-gray-600 text-[10px]">Amber rows = INITIATED or PROCESSING for 48h+ (SLA breach)</p>
    </div>
  )
}